import { Button, Form, Input, Modal, Popconfirm, Row } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { PaginationProps } from 'antd/lib/pagination';
import Table, { ColumnProps } from 'antd/lib/table';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import TencentCaptchaButton from 'src/components/common/TencentCaptchaButton';
import { IAgentInfo } from 'src/entity/AgentEntity';
import { IPageResult } from 'src/entity/BaseResult';
import { IDrugStoreInfo } from 'src/entity/DrugStoreEntity';
import { IVerificationCode } from 'src/entity/LoginParam';
import { PromotionListItem, PromotionSearchParam, PromotionStatusParams, UseLimitTypes } from 'src/entity/PromotionEntity';
import I18N from 'src/services/I18NService';
import PromotionService, { OrderTypeList } from 'src/services/PromotionService';
import PromotionSearch from './PromotionSearch';

interface IProps extends RouteComponentProps<{}, {}, {useCache: boolean}>, FormComponentProps<{}> {
  isDomestic: boolean,
  isAgent: boolean,
  userInfo: IAgentInfo | IDrugStoreInfo | undefined
}

const PromotionListPage: React.FC<IProps> = (props) => {
  const {
    isDomestic,
    isAgent,
    userInfo,
    form,
  } = props;
  const { getFieldDecorator } = form;
  
  if (!isDomestic || isAgent) {
    // 目前只支持配药中心
    return (
      <Row type="flex" align="top" style={{height: '100%', flexWrap: 'nowrap'}}>
        目前只支持境内配药中心
      </Row>
    )
  }
  // 当前登录人手机号
  let currentUserMobile = '';
  // 当前登录人手机号(脱密)
  let decryptionMobile = '';
  if (!!userInfo) {
    const { admin_mobile } = userInfo as IDrugStoreInfo; // TODO: 已有接口只能拿到此字段
    currentUserMobile = admin_mobile || '';
    decryptionMobile = currentUserMobile.substr(0, currentUserMobile.length - 8) + '****' + currentUserMobile.substr(currentUserMobile.length - 4);
  };
  // table 列配置
  const columns: Array<ColumnProps<PromotionListItem>> = useMemo(() => {
    const result: Array<ColumnProps<PromotionListItem>> = [
      {
        title: 'ID',
        key: 'id',
        dataIndex: 'id',
      },
      {
        title: '名称',
        key: 'name',
        dataIndex: 'name',
      },
      {
        title: '优惠面额',
        key: 'discount_price',
        dataIndex: 'discount_price',
      },
      {
        title: '满减面额',
        key: 'offer_price',
        dataIndex: 'offer_price',
      },
      {
        title: '适应订单类型',
        key: 'order_type',
        dataIndex: 'order_type',
        render: (text: any, record: PromotionListItem, index: number): React.ReactNode => {
          const resultArr: string[] = [];
          const { order_type } = record;
          if (Array.isArray(order_type)) {
            OrderTypeList.filter(item => {
              const isMatched = order_type.findIndex(typeItem => typeItem === item.value) !== -1;
              if (isMatched) {
                resultArr.push(item.name);
              }
            });
          }
          const resultStr = resultArr.join('、');
          return resultStr;
        },
      },
      {
        title: '使用限制',
        key: 'usage_condition',
        dataIndex: 'usage_condition',
        render: (text: any, record: PromotionListItem, index: number): React.ReactNode => {
          let resultStr = '';
          const { usage_condition } = record;
          if (usage_condition === UseLimitTypes.FirstOrder) {
            resultStr = '仅首单可用';
          } else if (usage_condition === UseLimitTypes.Unlimited) {
            resultStr = '不限';
          }
          return resultStr;
        }
      },
      {
        title: I18N.get('common_status'),
        key: 'is_active',
        dataIndex: 'is_active',
        render: (text: any, record: PromotionListItem, index: number): React.ReactNode => {
          const desc = record.is_active ? I18N.get('common_status_start') : I18N.get('common_status_disable');
          return (<span style={{color: record.is_active ? '#52c41a' : '#ff4d4f'}}>{desc}</span>)
        }
      },
      {
        title: I18N.get('common_operation'),
        dataIndex: 'operation',
        key: 'operation',
        render: (text: any, record: PromotionListItem, index: number): React.ReactNode => {
          const title = record.is_active ? 'common_status_disable' : 'common_status_start';
          return (
            <div>
              <a onClick={() => openModal(record)}>{I18N.get(title)}</a>
            </div>
          );
        },
      },
    ];
    return result;
  }, []);

  const [loading, setLoading] = useState(false);

  // 初始化用户查询参数
  const defaultQuery = new PromotionSearchParam();
  const [searchQuery, setQuery] = useState(defaultQuery as Partial<PromotionSearchParam>);

  // 初始化分页器配置
  const [pagination, setPagination] = useState(() => {
    const {limit, offset} = searchQuery;
    const current = Math.floor((offset as number) / (limit as number)) + 1;
    const pageSize = limit;
    const initialPagination = PromotionService.initPagination(current, pageSize);
    return initialPagination;
  });

  // 初始化用户列表数组
  const [list, setList] = useState([] as PromotionListItem[]);

  const queryRef = useRef<Partial<PromotionSearchParam>>();
  const paginationRef = useRef<PaginationProps>();

  // 控制模态窗显示
  const [modalVisible, setModalVisible] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  // 当前模态窗显示的数据
  const [detailRecord, setDetailRecord] = useState(undefined as PromotionListItem | undefined);


  useLayoutEffect(() => {
    queryRef.current = searchQuery;
    paginationRef.current = pagination;
  })


  useEffect(() => {
    // 第一次渲染时 => 查询 用户列表
    doSearch(searchQuery);
  }, []);


  /** 
   * 执行查询
   */
  const doSearch = async (query: Partial<PromotionSearchParam>) => {
    setLoading(true);
    let res: IPageResult<PromotionListItem[]> | undefined;
    res = await PromotionService.fetchPromotions(query);
    if (res) {
      const current = Math.floor((query.offset as number) / (query.limit as number)) + 1;
      const newPagination: PaginationProps = {
        ...pagination,
        current,
        pageSize: query.limit,
        total: res.count
      };
      setPagination(newPagination);
      setList(res.results);
    }
    setLoading(false);
  };

  /**
   * 搜索按钮点击回调函数
   */
   const handleSearch = useCallback((query: Partial<PromotionSearchParam>) => {
    // 需要获取 pageSize
    const {pageSize} = paginationRef.current as PaginationProps;
    query.offset = 0;
    query.limit = pageSize;
    setQuery(query);
    doSearch(query);
  }, []);

  /**
   * 分页器变化回调函数
   */
   const handlePaginationChange = (paginationTable: PaginationProps) => {
    const {current, pageSize} = paginationTable;
    const param: Partial<PromotionSearchParam> = {
      ...queryRef.current as Partial<PromotionSearchParam>,
      offset: ((current || 1) - 1) * (pageSize as number),
      limit: pageSize as number,
    }
    setQuery(param);
    doSearch(param);
  };

  /**
   * 点击[启用|禁用]开启模态窗
   * @param record 
   */
  const openModal = (record: PromotionListItem) => {
    setDetailRecord(record);
    setModalVisible(true);
  };

  /**
   * 关闭模态窗
   * @param record 
   */
  const closeModal = () => {
    setModalVisible(false);
    setDetailRecord(undefined);
  }

  /**
   * 用户[启用|禁用]状态切换
   */
  const handleStatusToggle = async () => {
    const { is_active, uuid} = detailRecord as PromotionListItem;
    let isSuccessed = false;
    form.validateFields(async (err, values) => {
      if (!err) {
        const code = (values as {code: string}).code;
        const params: PromotionStatusParams = {
          uuid,
          is_active: !is_active,
          phone: currentUserMobile,
          code,
        }
        setStatusLoading(true);
        isSuccessed = await PromotionService.togglePromotionStatus(params);
        setStatusLoading(false);
        if (isSuccessed) {
          setModalVisible(false);
          setList(oldList => {
            const newList: PromotionListItem[] = oldList.map(item => {
              const newItem = {...item};
              if (newItem.uuid === uuid) {
                newItem.is_active = !is_active;
              }
              return newItem;
            });
            return newList;
          });
        }
      }
    })
  }

  /**
   * 发送验证码
   * @param data
   */
  const verifyCode = async(data: IVerificationCode) => {
    if (data.mobile) {
      const param = {
        phone: data.mobile,
      };
      await PromotionService.sendVerifyCode(param);
    }
  }

  return (
    <div>
      <PromotionSearch
          defaultQuery={searchQuery}
          loading={loading}
          onSearch={handleSearch}/>
      <Table
        style={{marginTop: 24}}
        loading={loading}
        columns={columns}
        rowKey='uuid'
        pagination={pagination}
        dataSource={list}
        onChange={handlePaginationChange}
      />
      {detailRecord && (
        <Modal centered={true} title="提示" visible={modalVisible} destroyOnClose={true} footer={null} onCancel={closeModal}>
          <div style={{ marginBottom: 30, color: '#222' }}>
            确认
            <span style={{color: detailRecord.is_active ? '#ff4d4f' : '#52c41a'}}>{detailRecord.is_active ? '禁用' : '启用'}</span>
            满<span style={{color: '#00a2ff'}}>{detailRecord.offer_price}</span>
            减<span style={{color: '#00a2ff'}}>{detailRecord.discount_price}</span>
            的优惠政策
            （已优惠：<span style={{color: '#00a2ff'}}>{detailRecord.discount_rate}%</span>）？
          </div>
          <div style={{ marginBottom: 30, color: '#222' }}>
            将发送短信到{decryptionMobile}，请输入验证码：
          </div>
          <div style={{ marginBottom: 30, color: '#222' }}>
            <Form onSubmit={handleStatusToggle} >
              <Form.Item
                label={I18N.get('login_verify_code')}
                style={{ marginBottom: 0 }}
                labelAlign="left"
                labelCol={{span: 4}}
                wrapperCol={{span: 20}}
              >
                {getFieldDecorator('code', {
                  rules: [
                    {
                      required: true,
                      message: `${I18N.get('common_input')}${I18N.get('login_verify_code')}`,
                    },
                    {
                      pattern: /^[0-9]{6}$/gi,
                      message: '验证码格式错误',
                    },
                  ],
                })(
                  <Input
                    placeholder={`${I18N.get('common_input')}${I18N.get('login_verify_code')}`}
                    suffix={
                      <TencentCaptchaButton
                        isDomestic={true}
                        phoneOrEmail={currentUserMobile || ''}
                        onSend={(data) => verifyCode(data)}
                      />
                    }
                  />
                )}
              </Form.Item>
            </Form>
          </div>
          <div style={{ textAlign: 'center' }}>
            <Button type="primary" loading={statusLoading} onClick={handleStatusToggle}>
              {I18N.get('common_ok')}
            </Button>
            <Button style={{ marginLeft: 20 }} type="default" onClick={closeModal}>
              {I18N.get('common_cancel')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
const WrappedPromotionListComp = Form.create<IProps>()(PromotionListPage)
export default WrappedPromotionListComp;



// WEBPACK FOOTER //
// ./src/components/views/Promotion/PromotionListPage.tsx